import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * inspired by https://github.com/dynamicweb/swiffy-slider
 * latest commit used: https://github.com/dynamicweb/swiffy-slider/commit/afb6cfea3893c8646f61dd472651176718b92f3c
 * Motivation: SwiffySlider is chosen as a potential replacement for Swiper
 * due to its lightweight design and improved performance. The intention is
 * to gradually transition to SwiffySlider for a better user experience.
 */
import { useId } from 'react';
import classNames from 'classnames';
import { Indicators } from './Indicators';
import { Navigation } from './Navigation';
import { SlideItem } from './SlideItem';
import { useSwiffySlider } from './useSwiffySlider';
import styles from './SwiffySlider.module.scss';
export var NavigationShape;
(function (NavigationShape) {
    NavigationShape["Page"] = "sliderNavPage";
    NavigationShape["Noloop"] = "sliderNavNoloop";
    NavigationShape["Nodelay"] = "sliderNavNodelay";
    NavigationShape["Round"] = "sliderNavRound";
    NavigationShape["Square"] = "sliderNavSquare";
    NavigationShape["Touch"] = "sliderNavTouch";
    NavigationShape["Visible"] = "sliderNavVisible";
    NavigationShape["Outside"] = "sliderNavOutside";
    NavigationShape["OutsideExpand"] = "sliderNavOutsideExpand";
    NavigationShape["Scrollbar"] = "sliderNavScrollbar";
    NavigationShape["Dark"] = "sliderNavDark";
    NavigationShape["Autohide"] = "sliderNavAutohide";
})(NavigationShape || (NavigationShape = {}));
export var SliderRatio;
(function (SliderRatio) {
    SliderRatio["Default"] = "sliderItemRatio2x1";
    SliderRatio["Ratio32x9"] = "sliderItemRatio32x9";
    SliderRatio["Ratio21x9"] = "sliderItemRatio21x9";
    SliderRatio["Ratio2x1"] = "sliderItemRatio2x1";
    SliderRatio["Ratio16x9"] = "sliderItemRatio16x9";
    SliderRatio["Ratio4x3"] = "sliderItemRatio4x3";
    SliderRatio["Ratio1x1"] = "sliderItemRatio1x1";
    SliderRatio["Ratio3x4"] = "sliderItemRatio3x4";
    SliderRatio["RatioContain"] = "sliderItemRatioContain";
})(SliderRatio || (SliderRatio = {}));
export var SlidesPerPage;
(function (SlidesPerPage) {
    SlidesPerPage["Slides1"] = "sliderItemShow1";
    SlidesPerPage["Slides2"] = "sliderItemShow2";
    SlidesPerPage["Slides3"] = "sliderItemShow3";
    SlidesPerPage["Slides4"] = "sliderItemShow4";
    SlidesPerPage["Slides5"] = "sliderItemShow5";
    SlidesPerPage["Slides6"] = "sliderItemShow6";
})(SlidesPerPage || (SlidesPerPage = {}));
export var IndicatorShape;
(function (IndicatorShape) {
    IndicatorShape["Dash"] = "sliderIndicatorsDash";
    IndicatorShape["Round"] = "sliderIndicatorsRound";
    IndicatorShape["Square"] = "sliderIndicatorsSquare";
})(IndicatorShape || (IndicatorShape = {}));
export var IndicatorVariant;
(function (IndicatorVariant) {
    IndicatorVariant["Default"] = "sliderIndicatorsDefault";
    IndicatorVariant["Outside"] = "sliderIndicatorsOutside";
    IndicatorVariant["Dark"] = "sliderIndicatorsDark";
    IndicatorVariant["Brand"] = "sliderIndicatorsBrand";
    IndicatorVariant["Highlight"] = "sliderIndicatorsHighlight";
})(IndicatorVariant || (IndicatorVariant = {}));
const SwiffySlider = ({ autopause = true, autoplay = true, autoplayInterval = 2_500, debug = false, indicator = {
    shape: IndicatorShape.Dash,
    variant: IndicatorVariant.Default,
    isMobileVisible: false,
}, navigation = NavigationShape.Round, nodelay = true, noloop, ratio = SliderRatio.Default, showIndicators = true, showNavigation = true, showPartialReveal = false, slides, slidesPerPage = 1, }) => {
    const sliderItemId = useId();
    const indicatorsStyles = classNames({ [styles.sliderIndicatorsSm]: indicator.isMobileVisible }, styles[indicator?.shape || IndicatorShape.Dash], Array.isArray(indicator.variant)
        ? indicator?.variant?.map(variant => styles[variant])
        : styles[indicator?.variant || IndicatorVariant.Default]);
    const { clonedSlides, containerRef, indicatorsRef, slide, slideTo, slidesLength } = useSwiffySlider({
        slides,
        nodelay,
        noloop,
        autoplay,
        autopause,
        autoplayInterval,
    });
    return (_jsxs("div", { className: classNames(styles.swiffySlider, styles[navigation], styles[ratio], 
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        styles[SlidesPerPage[`Slides${slidesPerPage}`]], indicatorsStyles, {
            [styles.sliderItemReveal]: showPartialReveal,
            [styles.sliderItemHelper]: debug,
            [styles.loop]: !noloop,
        }), children: [_jsx("ul", { className: `${styles.sliderContainer} m-0 p-0`, ref: containerRef, children: clonedSlides.map((slideItem, index) => (
                // eslint-disable-next-line react/no-array-index-key
                _jsx(SlideItem, { children: slideItem }, `${sliderItemId}-${index}`))) }), showNavigation && _jsx(Navigation, { onSlide: slide }), showIndicators && (_jsx(Indicators, { size: slidesLength, onSlideTo: slideTo, ref: indicatorsRef, currentIndex: 0 }))] }));
};
export { SwiffySlider };
